import { useState, useRef, useEffect, useMemo, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Field, controlBase, controlSize, type ControlSize } from './Field';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: readonly ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: ControlSize;
  /** Show the X that empties the field. Off for a required field, where empty is not a choice. */
  clearable?: boolean;
  /** Set false only for a list nobody would ever type into. On by default. */
  searchable?: boolean;
  /** Layout for the field as a whole — grid span, width. */
  className?: string;
}

/**
 * A list you can search, arrow through and read on both themes.
 *
 * A native <select> was what the master screens used, and it looks like whatever the operating
 * system decides: on Windows the chevron sits hard against the edge with padding we do not control,
 * and there is no way to search it, so a currency list is scrolled rather than typed. This is the
 * replacement, and it takes its height, border, focus ring and disabled treatment from the same
 * `controlBase`/`controlSize` the Input and Textarea use, so a dropdown lines up with the text field
 * beside it instead of merely looking close.
 */
const ROW_PX = 36;

export const Combobox = ({
  options,
  value,
  onChange,
  label,
  error,
  hint,
  placeholder = 'Select…',
  disabled = false,
  size = 'sm',
  clearable = true,
  searchable = true,
  className,
}: ComboboxProps) => {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<DOMRect | null>(null);

  const selected = options.find(o => o.value === value);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [options, filter]);

  const close = () => { setOpen(false); setFilter(''); };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      // The panel is portalled to <body>, so it is not inside containerRef in the DOM.
      if (!containerRef.current?.contains(t) && !panelRef.current?.contains(t)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Where the panel goes. Measured rather than assumed: these fields sit near the bottom of long
  // setup forms as often as not, and a list rendered off the bottom of the window is unusable.
  const measure = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setBox(r);
    const panel = (searchable ? 52 : 0) + Math.min(options.length, 6) * ROW_PX + 34;
    setDropUp(window.innerHeight - r.bottom < panel && r.top > panel);
  }, [options.length, searchable]);

  // On open: measure, start on the current selection, and focus the search box.
  useEffect(() => {
    if (!open) return;
    measure();
    setActive(Math.max(0, options.findIndex(o => o.value === value)));
    if (searchable) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // A fixed panel does not travel with the field, so re-measure when anything moves it. `true`
  // catches scrolling in a scroll container, not just the window.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  // Keep the highlighted row on screen while arrowing through a long list.
  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const commit = (opt?: ComboboxOption) => {
    if (opt) onChange(opt.value);
    close();
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Escape':
        if (open) { e.preventDefault(); commit(); }
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        if (!open) { setOpen(true); break; }
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActive(i => (filtered.length === 0 ? 0 : (i + step + filtered.length) % filtered.length));
        break;
      }
      case 'Home': if (open) { e.preventDefault(); setActive(0); } break;
      case 'End':  if (open) { e.preventDefault(); setActive(filtered.length - 1); } break;
      case 'Enter':
        // Only swallow Enter while the list is open, so the key still submits the form otherwise.
        if (open) { e.preventDefault(); commit(filtered[active]); }
        break;
      case 'Tab':
        if (open) close();
        break;
    }
  };

  const showClear = clearable && !!selected && !disabled;

  return (
    <Field label={label} htmlFor={uid} error={error} hint={hint} className={className}>
      {/* `relative` is load-bearing: the panel below is absolutely positioned, and without a
          positioned ancestor here it anchors to whatever is positioned further up the tree — which
          rendered it full-width across the bottom of the page instead of under the field. */}
      <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
        <button
          ref={triggerRef}
          id={uid}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={`${uid}-list`}
          aria-activedescendant={open ? `${uid}-opt-${active}` : undefined}
          disabled={disabled}
          onClick={() => !disabled && (open ? close() : setOpen(true))}
          className={cn(
            controlBase(!!error),
            controlSize[size],
            'pl-3 text-left',
            showClear ? 'pr-14' : 'pr-8',
            open && !error && 'border-brand-400 ring-2 ring-brand-500',
            !selected && 'text-gray-400 dark:text-gray-500',
          )}
        >
          <span className="block truncate">{selected?.label ?? placeholder}</span>
        </button>

        {/* Outside the trigger, not inside it: a control nested in a button is unreachable by
            keyboard and confuses a screen reader about what it is about to activate. */}
        {showClear && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => { onChange(''); triggerRef.current?.focus(); }}
            className="absolute right-7 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={12} />
          </button>
        )}
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 transition-transform',
            open && 'rotate-180',
          )}
        />

        {/* Portalled to <body> and positioned fixed. Absolutely positioning it inside the field put
            it at the mercy of every ancestor: a card or tab panel with `overflow: hidden` clipped
            the top of the panel clean off when it opened upward, taking the search box with it. */}
        {open && box && createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: box.left,
              width: box.width,
              ...(dropUp
                ? { bottom: window.innerHeight - box.top + 4 }
                : { top: box.bottom + 4 }),
            }}
            className={cn(
              'z-[200] min-w-[220px] rounded-md border shadow-xl',
              'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
            )}
          >
            {searchable && (
              <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                <div className="relative">
                  <Search size={13} aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={filter}
                    onChange={e => { setFilter(e.target.value); setActive(0); }}
                    placeholder="Search…"
                    aria-label="Search options"
                    className={cn(controlBase(), 'h-8 pl-7 pr-3 text-sm')}
                  />
                </div>
              </div>
            )}

            <div ref={listRef} id={`${uid}-list`} role="listbox" className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">No results found</div>
              ) : (
                filtered.map((opt, i) => (
                  <div
                    key={opt.value}
                    id={`${uid}-opt-${i}`}
                    role="option"
                    aria-selected={opt.value === value}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => commit(opt)}
                    className={cn(
                      'px-3 py-1.5 text-sm cursor-pointer truncate',
                      opt.value === value
                        ? 'text-brand-700 dark:text-brand-300 font-medium'
                        : 'text-gray-700 dark:text-gray-200',
                      i === active && 'bg-brand-50 dark:bg-brand-900/30',
                    )}
                  >
                    {opt.label}
                  </div>
                ))
              )}
            </div>

            {searchable && (
              <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 text-right">
                {filtered.length} of {options.length}
              </div>
            )}
          </div>,
          document.body,
        )}
      </div>
    </Field>
  );
};
